import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CONTACT, whatsappLink } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_CLASS =
  "rounded-none border-white/30 bg-white/5 text-white placeholder:text-white/40 focus-visible:ring-brand-orange";

export default function Contact({ onBookNow }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "", moto_type: "" });
  const [motos, setMotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/motos`)
      .then((r) => setMotos(r.data.motos || []))
      .catch(() => setMotos([]));
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("All fields required");
      return;
    }
    setSubmitting(true);
    try {
      const fullMessage = form.moto_type
        ? `[Interested in: ${form.moto_type}]\n\n${form.message}`
        : form.message;
      await axios.post(`${API}/contacts`, {
        name: form.name,
        email: form.email,
        message: fullMessage,
        language: i18n.language,
      });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "", moto_type: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-page" className="bg-brand-black py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
              {t("nav.contact")}
            </span>
            <h1 className="mt-2 font-display text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
              {t("contact.title")}
            </h1>
            <p className="mt-4 text-base text-white/75 sm:text-lg">{t("contact.subtitle")}</p>

            <ul className="mt-8 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-brand-orange" />
                <span>{CONTACT.city}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 shrink-0 text-brand-orange" />
                <a href={whatsappLink()} className="hover:text-brand-orange">
                  {CONTACT.whatsappDisplay}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 shrink-0 text-brand-orange" />
                <a href={`mailto:${CONTACT.email}`} className="break-all hover:text-brand-orange">
                  {CONTACT.email}
                </a>
              </li>
            </ul>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center border-2 border-brand-orange hover:bg-brand-orange hover:text-white"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center border-2 border-brand-orange hover:bg-brand-orange hover:text-white"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 items-center justify-center gap-2 border-2 border-[#25D366] bg-[#25D366] px-4 text-sm font-bold uppercase tracking-widest text-white hover:bg-brand-black hover:text-[#1ea952]"
              >
                {t("contact.whatsappBtn")}
              </a>
              <Button
                data-testid="contact-page-book-btn"
                onClick={() => onBookNow?.()}
                className="h-11 rounded-none bg-brand-black font-display text-base uppercase tracking-wider text-white hover:bg-brand-orange"
              >
                {t("nav.bookNow")}
              </Button>
            </div>
          </div>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="md:col-span-7 space-y-4 border-2 border-brand-orange bg-brand-black p-6 hard-shadow-orange md:p-8"
          >
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-white">{t("contact.name")} *</Label>
              <Input data-testid="contact-name" value={form.name} onChange={setField("name")} required className={FIELD_CLASS} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-white">{t("contact.email")} *</Label>
              <Input data-testid="contact-email" type="email" value={form.email} onChange={setField("email")} required className={FIELD_CLASS} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-white">{t("contact.moto")}</Label>
              <Select
                value={form.moto_type}
                onValueChange={(v) => setForm((f) => ({ ...f, moto_type: v }))}
              >
                <SelectTrigger data-testid="contact-moto-select" className={`${FIELD_CLASS} h-10`}>
                  <SelectValue placeholder={t("contact.moto")} />
                </SelectTrigger>
                <SelectContent className="rounded-none border-brand-orange bg-brand-black text-white">
                  {motos.map((m) => (
                    <SelectItem
                      key={m.id}
                      value={m.name}
                      className="rounded-none text-white focus:bg-brand-orange focus:text-white"
                    >
                      {m.name} — ${m.price_day_usd}/day
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-white">{t("contact.message")} *</Label>
              <Textarea data-testid="contact-message" value={form.message} onChange={setField("message")} rows={5} required className={FIELD_CLASS} />
            </div>
            <Button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={submitting}
              className="h-12 w-full rounded-none bg-brand-orange font-display text-xl uppercase tracking-wider text-white hover:bg-brand-black"
            >
              {submitting ? "…" : t("contact.submit")}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
